import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {FileText, Home, DollarSign, Layers} from 'lucide-react';
import toast from "react-hot-toast";
import { useGuias } from "../context/GuiasContext";

interface LoginPageProps {
  onLogin: (role: "admin" | "user", nomeCompleto: string) => void;
}

// Componente Carousel de Features
function CarouselFeatures() {
  const [currentIndex, setCurrentIndex] = useState(0);

  const features = [
    {
      icon: Layers,
      title: "Cadastros Diversos",
      description: "Feirantes, vendedores e aforamentos"
    },
    {
      icon: FileText,
      title: "Guias de Sepultamento",
      description: "Emissão e gestão completa"
    },
    {
      icon: Home,
      title: "Aforamentos",
      description: "Controle de propriedades municipais"
    },
    {
      icon: DollarSign,
      title: "Pagamentos PIX",
      description: "Geração de QR Code e comprovantes"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [features.length]);

  return (
    <div className="flex flex-col items-center justify-center h-full px-3 lg:px-4 pt-12">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center text-center"
        >
          {React.createElement(features[currentIndex].icon, {
            className: "w-10 h-10 lg:w-12 lg:h-12 text-white mb-3 lg:mb-4"
          })}
          <h2 className="text-white text-base lg:text-xl font-bold mb-2">
            {features[currentIndex].title}
          </h2>
          <p className="text-blue-100 text-xs lg:text-sm">
            {features[currentIndex].description}
          </p>
        </motion.div>
      </AnimatePresence>
      
      {/* Indicadores */}
      <div className="flex gap-2 mt-6">
        {features.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentIndex ? "bg-white w-6" : "bg-white/40"
            }`}
          />
        ))}
      </div>
      
      <p className="text-white/60 text-xs mt-6 text-center">
        Prefeitura Municipal de São José do Seridó/RN
      </p>
    </div>
  );
}

export default function LoginPage({ onLogin }: LoginPageProps) {
  const { validateLogin, logoUrl } = useGuias();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    setTimeout(() => {
      const result = validateLogin(username, password);
      if (result.success && result.user) {
        toast.success(`Bem-vindo, ${result.user.nomeCompleto}!`);
        onLogin(result.user.role, result.user.nomeCompleto);
      } else {
        toast.error("Usuário ou senha incorretos");
      }
      setLoading(false);
    }, 800);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
      {/* Formas decorativas de fundo */}
      <div className="absolute top-0 left-0 w-48 h-48 sm:w-64 sm:h-64 lg:w-96 lg:h-96 bg-[#004161] rounded-full -translate-x-1/2 -translate-y-1/2 opacity-20"></div>
      <div className="absolute bottom-0 right-0 w-64 h-64 sm:w-80 sm:h-80 lg:w-[500px] lg:h-[500px] bg-yellow-400 rounded-full translate-x-1/3 translate-y-1/3 opacity-30"></div>
      <div className="absolute top-1/4 right-1/4 w-16 h-16 sm:w-24 sm:h-24 lg:w-32 lg:h-32 bg-[#004161] rounded-full opacity-10"></div>
      
      <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-6xl bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden">
          
          <div className="grid lg:grid-cols-2 gap-0">
            {/* Lado Esquerdo - Formulário */}
            <div className="p-6 sm:p-10 lg:p-16 flex flex-col justify-center bg-gray-50">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}>
                
                <div className="mb-6 sm:mb-8 flex justify-center">
                  <img
                    src={logoUrl}
                    alt="Logo do Município"
                    className="w-20 h-20 sm:w-28 sm:h-28 lg:w-32 lg:h-32 object-contain drop-shadow-xl" />

                </div>
                
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#004161] mb-2 tracking-wide">BEM-VINDO</h1>
                <p className="text-gray-500 mb-8 sm:mb-10 lg:mb-12 text-xs sm:text-sm">Sistema de Gestão e Emissão de Guias</p>

                <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                  <div>
                    <label htmlFor="username" className="block text-xs sm:text-sm font-medium text-[#004161] mb-2">
                      Usuário
                    </label>
                    <input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full px-0 py-2 sm:py-3 bg-transparent border-b-2 border-gray-300 text-sm sm:text-base text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#004161] transition-colors"
                      placeholder="Digite seu usuário"
                      required />

                  </div>

                  <div>
                    <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-[#004161] mb-2">
                      Senha
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-0 py-2 sm:py-3 bg-transparent border-b-2 border-gray-300 text-sm sm:text-base text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#004161] transition-colors"
                      placeholder="••••••••"
                      required />

                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={loading}
                    className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-800 font-bold py-3 sm:py-4 rounded-lg shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-6 sm:mt-8 text-sm sm:text-base">
                    {loading ? "ENTRANDO..." : "ENTRAR"}
                  </motion.button>
                </form>

                <p className="text-center text-gray-400 text-xs mt-6 sm:mt-8">© 2026 Sistema EMIT - Todos os direitos reservados | Desenvolvimento Railson Abreu

                </p>
              </motion.div>
            </div>

            {/* Lado Direito - Ilustração */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="hidden lg:flex items-center justify-center p-12 xl:p-16 bg-white relative">
              
              {/* Elementos decorativos */}
              <div className="absolute top-6 right-6 lg:top-8 lg:right-8 w-10 h-10 lg:w-12 lg:h-12 border-2 border-gray-200 rounded-lg rotate-12"></div>
              <div className="absolute bottom-12 left-8 lg:bottom-16 lg:left-12 w-6 h-6 lg:w-8 lg:h-8 border-2 border-gray-200 rounded-lg -rotate-12"></div>
              <div className="absolute top-1/3 left-6 lg:left-8 w-5 h-5 lg:w-6 lg:h-6 bg-yellow-200 rounded-full opacity-50"></div>
              <div className="absolute bottom-1/4 right-12 lg:right-16 w-8 h-8 lg:w-10 lg:h-10 bg-[#004161] opacity-10 rounded-full"></div>
              
              <div className="relative z-10">
                {/* Monitor/Tela */}
                <div className="relative">
                  <div className="w-80 h-56 lg:w-96 lg:h-64 bg-[#004161] rounded-t-2xl shadow-2xl relative overflow-hidden">
                    {/* Barra superior do navegador */}
                    <div className="absolute top-0 left-0 right-0 h-8 bg-[#003450] flex items-center px-4 space-x-2">
                      <div className="w-3 h-3 rounded-full bg-red-400"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-300"></div>
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    </div>
                    
                    {/* Conteúdo da tela - Carousel Animado */}
                    <CarouselFeatures />
                  </div>
                  
                  {/* Base do monitor */}
                  <div className="w-28 h-20 lg:w-32 lg:h-24 mx-auto">
                    <div className="w-3 h-12 lg:w-4 lg:h-16 bg-gray-300 mx-auto"></div>
                    <div className="w-full h-3 lg:h-4 bg-gray-400 rounded-b-lg"></div>
                  </div>
                </div>

                {/* Elementos decorativos de folhas */}
                <div className="absolute -bottom-6 -right-6 lg:-bottom-8 lg:-right-8">
                  <svg width="100" height="100" viewBox="0 0 120 120" className="text-gray-200 opacity-40 lg:w-[120px] lg:h-[120px]">
                    <path d="M60 10 Q80 30 70 50 Q60 70 60 90 Q40 70 50 50 Q60 30 60 10" fill="currentColor" />
                    <path d="M60 10 Q40 30 50 50 Q60 70 60 90 Q80 70 70 50 Q60 30 60 10" fill="currentColor" opacity="0.7" />
                  </svg>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>);

}